import * as React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { withTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import { useCustomersReport } from '../hooks/useReportsApi';

const COLORS = ['#3182ce', '#10b981', '#f59e0b', '#ef4444'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

function CustomersTab({ t, dateParams }) {
  const { data, loading, error } = useCustomersReport(dateParams);

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  return (
    <Box>
      {/* Export Buttons */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <ExportButtons reportType="customers" params={dateParams} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={PeopleIcon}
            label={t('reports.customers.total')}
            value={data?.total}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={PersonIcon}
            label={t('reports.customers.individual')}
            value={data?.individual}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={BusinessIcon}
            label={t('reports.customers.company')}
            value={data?.company}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={PersonAddIcon}
            label={t('reports.customers.newThisPeriod')}
            value={data?.new_this_period}
            loading={loading}
            iconColor="success.main"
            iconBgColor="rgba(16, 185, 129, 0.1)"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Growth Trend */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.customers.growthTrend')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data?.growth_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period_label" />
                  <YAxis />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="total"
                    name={t('reports.customers.totalCustomers')}
                    stroke="#3182ce"
                    fill="rgba(49, 130, 206, 0.2)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="new"
                    name={t('reports.customers.newCustomers')}
                    stroke="#10b981"
                    fill="rgba(16, 185, 129, 0.2)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Type Breakdown */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.customers.typeBreakdown')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (data?.type_breakdown || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.type_breakdown}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="type_display"
                  >
                    {data.type_breakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value, entry) => `${value} (${entry.payload?.count ?? 0})`}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t('reports.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Average Revenue */}
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('reports.customers.avgRevenuePerCustomer')}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
            {loading ? '-' : formatCurrency(data?.avg_revenue_per_customer || 0)}
          </Typography>
        </Box>
      </Paper>

      {/* Top Customers Table */}
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {t('reports.customers.topCustomers')}
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" height={300} />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.customers.name')}</TableCell>
                  <TableCell>{t('reports.customers.type')}</TableCell>
                  <TableCell align="right">{t('reports.customers.orders')}</TableCell>
                  <TableCell align="right">{t('reports.customers.avgOrder')}</TableCell>
                  <TableCell align="right">{t('reports.customers.revenue')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.top_customers || []).map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500 }}>{customer.name}</Typography>
                    </TableCell>
                    <TableCell>{customer.type_display}</TableCell>
                    <TableCell align="right">{customer.orders}</TableCell>
                    <TableCell align="right">{formatCurrency(customer.avg_order)}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 600, color: 'secondary.main' }}>
                        {formatCurrency(customer.revenue)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {(!data?.top_customers || data.top_customers.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                      {t('reports.noData')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default withTranslation()(CustomersTab);
